import { Visit } from "@cur8/rich-entity";
import { useTodaysScheduleQuery } from "hooks/queries/useTodaysScheduleQuery";
import { useVisitQueries } from "hooks/queries/useVisitQuery";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { useEffect, useMemo, useRef } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";

export interface TodaysBookedVisits {
  visits: Visit[];
  isPending: boolean;
  // True only when the whole day is unavailable. A day that loaded with gaps in
  // it is a success.
  isError: boolean;
  retry(): void;
}

// The last gap filed per site. Outside the hook because the two views that
// share it each mount their own copy, and a gap is not news again just because
// the operator walked from one screen to the other.
const lastTracedGap = new Map<string, string>();

/**
 * Today's bookings for the selected site, shared by the two views that pick a
 * member out of the day's schedule.
 *
 * Gaps within the day are not surfaced: nothing the operator does closes them.
 * They are traced instead, so a gap nobody sees is still a gap someone can
 * count.
 */
export function useTodaysBookedVisits(): TodaysBookedVisits {
  const siteId = getAndParseLocationCookie()?.siteId;
  const appInsights = useAppInsights();
  const scheduleQuery = useTodaysScheduleQuery({ siteId });
  const bookedSlots = scheduleQuery.data?.bookedSlots;

  // A slot that outlived its member has no visit to fetch, so it is never asked
  // for: the request would spend its retries on an id known to be dead, and
  // stall the list behind them.
  const openableSlots = useMemo(
    () =>
      bookedSlots?.filter(
        (slot) => slot.visitId != null && slot.patientId != null
      ),
    [bookedSlots]
  );

  const visitQueries = useVisitQueries(openableSlots);
  const visits = visitQueries.flatMap((query) => query.data ?? []);

  // Named, not just counted: a count says how often bookings outlive their
  // member, the ids say which bookings to go and unassign.
  const deadSlots = useMemo(
    () =>
      bookedSlots
        ?.filter((slot) => slot.visitId == null || slot.patientId == null)
        .map((slot) => slot.slotId) ?? [],
    [bookedSlots]
  );
  const unreadableVisitCount = visitQueries.filter(
    (query) => query.isError
  ).length;
  const failedWindows = scheduleQuery.data?.failedWindows ?? [];
  const failureReasons = scheduleQuery.data?.failureReasons ?? [];

  const windows = failedWindows
    .map((window) => `${window.start.toISO()}/${window.end.toISO()}`)
    .join(",");
  const minutesUnread = failedWindows.reduce(
    (total, window) => total + window.end.diff(window.start, "minutes").minutes,
    0
  );

  // Only once the day has settled, so a visit still in flight is not counted as
  // one that failed.
  const settled =
    scheduleQuery.isSuccess && visitQueries.every((query) => !query.isPending);
  // What is missing, as one string. A re-render or a second mount leaves it
  // unchanged and files nothing; a changed gap is a changed key and worth its
  // own line.
  const gapKey =
    settled &&
    (windows !== "" || deadSlots.length > 0 || unreadableVisitCount > 0)
      ? [windows, deadSlots.join(","), unreadableVisitCount].join("|")
      : undefined;

  // Read through a ref so the effect depends on the key alone. Listing the
  // numbers would fire it on every render, since visits is a new array each
  // time, and they cannot change without the key changing anyway.
  const gapProperties = {
    siteId,
    failedWindowCount: failedWindows.length,
    minutesUnread,
    deadSlotCount: deadSlots.length,
    unreadableVisitCount,
    bookingsShown: visits.length,
    // Bisect boundaries, so they say which part of the day went missing, not
    // which bookings did.
    windows,
    deadSlots: deadSlots.join(","),
    // What refused the windows, so a day the endpoint 500s on is not filed as
    // the same kind of problem as a day the iPad could not reach.
    failureReasons: failureReasons.join(","),
  };
  const latestProperties = useRef(gapProperties);
  latestProperties.current = gapProperties;

  useEffect(
    function traceTheGap() {
      const site = siteId ?? "unknown";

      if (gapKey == null) {
        // The day is whole, so a gap that opens after this one is a new
        // episode rather than the one already filed.
        lastTracedGap.delete(site);
        return;
      }

      if (lastTracedGap.get(site) === gapKey) {
        return;
      }
      lastTracedGap.set(site, gapKey);

      appInsights.trackTrace({
        message: "Today's schedule loaded with gaps",
        severityLevel: SeverityLevel.Warning,
        properties: latestProperties.current,
      });
    },
    [gapKey, siteId, appInsights]
  );

  // The schedule said the day had bookings and not one of them can be shown,
  // whatever mix of dead slots and failed visit reads got it there. From the
  // operator's side that is the same outage as a schedule that never loaded,
  // and it must not render as a clear day.
  const nothingReadable =
    settled && (bookedSlots?.length ?? 0) > 0 && visits.length === 0;

  return {
    visits,
    // Hold the spinner only while there is nothing yet to show, so one slow
    // visit cannot keep the whole list hidden.
    isPending:
      scheduleQuery.isPending ||
      (visits.length === 0 && visitQueries.some((query) => query.isPending)),
    // A day that read nothing at all is an outage, not an empty schedule, even
    // though the bisect itself completed without throwing.
    isError:
      scheduleQuery.isError ||
      scheduleQuery.data?.failedEntirely === true ||
      nothingReadable,
    retry: () => {
      void scheduleQuery.refetch();
      // The visit reads are what failed in the nothingReadable case, and a
      // fresh schedule alone would hand them back their cached error, leaving
      // Retry looking broken.
      for (const query of visitQueries) {
        if (query.isError) {
          void query.refetch();
        }
      }
    },
  };
}
